import { useEffect } from "react";
import { BRAND } from "@/branding.config";

/**
 * SEO head manager. Sets title, description, canonical, OG/Twitter tags and
 * a JSON-LD structured data block. Values default to central BRAND config.
 */
export default function HeadMeta({ title, description, path, jsonLd, image }) {
  useEffect(() => {
    const fullTitle = title ? `${title} · ${BRAND.app_name}` : BRAND.default_meta_title;
    document.title = fullTitle;

    const setMeta = (name, content, attr = "name") => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const desc = description || BRAND.default_meta_description;
    const og = image || BRAND.default_og_image;
    setMeta("description",       desc);
    setMeta("theme-color",       BRAND.primary_color);
    setMeta("og:title",          fullTitle,               "property");
    setMeta("og:description",    desc,                    "property");
    setMeta("og:site_name",      BRAND.app_name,          "property");
    setMeta("og:image",          og,                      "property");
    setMeta("og:type",           "website",               "property");
    setMeta("twitter:card",      "summary_large_image");
    setMeta("twitter:title",     fullTitle);
    setMeta("twitter:description", desc);
    setMeta("twitter:image",     og);

    // Canonical
    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    const canonicalHref = (BRAND.website_url || "").replace(/\/$/, "") + (path || (typeof window !== "undefined" ? window.location.pathname : "/"));
    canonical.setAttribute("href", canonicalHref);

    // Favicon (idempotent)
    let icon = document.head.querySelector("link[rel='icon']");
    if (!icon) {
      icon = document.createElement("link");
      icon.setAttribute("rel", "icon");
      document.head.appendChild(icon);
    }
    icon.setAttribute("href", BRAND.favicon);

    // JSON-LD
    let ld = document.head.querySelector("script[type='application/ld+json'][data-vela='page']");
    if (!ld) {
      ld = document.createElement("script");
      ld.setAttribute("type", "application/ld+json");
      ld.setAttribute("data-vela", "page");
      document.head.appendChild(ld);
    }
    const defaultLd = {
      "@context": "https://schema.org",
      "@type":    "Organization",
      "name":     BRAND.company_name,
      "url":      BRAND.website_url,
      "logo":     BRAND.logo,
      "description": BRAND.default_meta_description,
      "email":    BRAND.contact_email,
    };
    ld.textContent = JSON.stringify(jsonLd || defaultLd);
  }, [title, description, path, jsonLd, image]);

  return null;
}

/**
 * Injects brand colors into CSS variables. Runs once at boot.
 */
export function BrandThemeVars() {
  useEffect(() => {
    const hex = (h) => h.replace("#", "");
    const r = (h) => parseInt(hex(h).slice(0, 2), 16);
    const g = (h) => parseInt(hex(h).slice(2, 4), 16);
    const b = (h) => parseInt(hex(h).slice(4, 6), 16);
    const toHsl = (h) => {
      const rr = r(h) / 255, gg = g(h) / 255, bb = b(h) / 255;
      const max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb);
      const l = (max + min) / 2;
      let hh = 0, ss = 0;
      if (max !== min) {
        const d = max - min;
        ss = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case rr: hh = (gg - bb) / d + (gg < bb ? 6 : 0); break;
          case gg: hh = (bb - rr) / d + 2; break;
          default: hh = (rr - gg) / d + 4;
        }
        hh /= 6;
      }
      return `${Math.round(hh * 360)} ${Math.round(ss * 100)}% ${Math.round(l * 100)}%`;
    };
    const root = document.documentElement;
    root.style.setProperty("--primary",   toHsl(BRAND.primary_color));
    root.style.setProperty("--secondary", toHsl(BRAND.secondary_color));
    root.style.setProperty("--accent",    toHsl(BRAND.accent_color));
    root.style.setProperty("--ring",      toHsl(BRAND.primary_color));
  }, []);
  return null;
}
